import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hasConsentChoice, acceptAll, rejectAll, saveConsent, getConsent } from '../../utils/cookieConsent';

const CookieConsent = () => {
  const { t } = useTranslation('pages');
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Show banner only if no choice made yet
    if (!hasConsentChoice()) {
      // Small delay so it doesn't flash on load
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  if (!visible) return null;

  const handleAcceptAll = () => {
    acceptAll();
    setVisible(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setVisible(false);
  };

  const handleSaveCustom = () => {
    saveConsent({ analytics, marketing });
    setVisible(false);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 pb-[calc(env(safe-area-inset-bottom,0px)+16px)]">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-[0_-4px_24px_rgba(0,0,0,0.12)] border border-[#e2e1df] overflow-hidden">
        <div className="p-5">
          {/* Main text */}
          <p className="text-[14px] leading-[22px] text-[#0d0c07] mb-1">
            {t('cookieConsent.message')}{' '}
            <Link to="/cookies" className="underline text-[#0d0c07] font-semibold">
              {t('cookieConsent.learnMore')}
            </Link>
          </p>

          {/* Customize panel */}
          {showCustomize && (
            <div className="mt-4 flex flex-col gap-3">
              {/* Necessary — always on */}
              <label className="flex items-center justify-between">
                <span className="text-[13px] text-[#0d0c07]">{t('cookieConsent.necessary')}</span>
                <div className="w-10 h-[22px] rounded-full bg-[#006600] relative pointer-events-none">
                  <div className="absolute right-[3px] top-[3px] w-4 h-4 rounded-full bg-white" />
                </div>
              </label>

              {/* Analytics */}
              <label className="flex items-center justify-between cursor-pointer" onClick={() => setAnalytics(!analytics)}>
                <span className="text-[13px] text-[#0d0c07]">{t('cookieConsent.analytics')}</span>
                <div className={`w-10 h-[22px] rounded-full relative transition-colors ${analytics ? 'bg-[#006600]' : 'bg-[#c1c0be]'}`}>
                  <div className={`absolute top-[3px] w-4 h-4 rounded-full bg-white transition-all ${analytics ? 'right-[3px]' : 'left-[3px]'}`} />
                </div>
              </label>

              {/* Marketing */}
              <label className="flex items-center justify-between cursor-pointer" onClick={() => setMarketing(!marketing)}>
                <span className="text-[13px] text-[#0d0c07]">{t('cookieConsent.marketing')}</span>
                <div className={`w-10 h-[22px] rounded-full relative transition-colors ${marketing ? 'bg-[#006600]' : 'bg-[#c1c0be]'}`}>
                  <div className={`absolute top-[3px] w-4 h-4 rounded-full bg-white transition-all ${marketing ? 'right-[3px]' : 'left-[3px]'}`} />
                </div>
              </label>
            </div>
          )}

          {/* Buttons */}
          <div className="mt-4 flex flex-col gap-2">
            {showCustomize ? (
              <button
                onClick={handleSaveCustom}
                className="w-full h-11 rounded-xl bg-[#0d0c07] text-white text-[14px] font-semibold"
              >
                {t('cookieConsent.savePreferences')}
              </button>
            ) : (
              <>
                <button
                  onClick={handleAcceptAll}
                  className="w-full h-11 rounded-xl bg-[#0d0c07] text-white text-[14px] font-semibold"
                >
                  {t('cookieConsent.acceptAll')}
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={handleRejectAll}
                    className="flex-1 h-11 rounded-xl border border-[#c1c0be] text-[#0d0c07] text-[14px] font-semibold"
                  >
                    {t('cookieConsent.rejectAll')}
                  </button>
                  <button
                    onClick={() => setShowCustomize(true)}
                    className="flex-1 h-11 rounded-xl border border-[#c1c0be] text-[#0d0c07] text-[14px] font-semibold"
                  >
                    {t('cookieConsent.customize')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
